/**
 * @file     VowLockedFacts.tsx
 * @purpose  Blurred teaser rows for VOW-only listing facts. A locked value is
 *           a drawn bar of a fixed width per row kind, never text derived from
 *           the real value (the server never sent it: the listing carries only
 *           a names-only `vowFieldsPresent` list). VowDeepDiveFacts draws the
 *           rooms, finished areas, systems and lot rows (real values once VOW
 *           access arrives, bars before), useVowTeaserGate opens VowAccessModal
 *           at the right step (account signed out, terms and phone signed in)
 *           from every teaser row (each row is one button) and from
 *           VowUnlockCta, the section's call to action, and
 *           useVowUnlockedDetail refetches a client-loaded record in place
 *           on unlock.
 */
'use client';

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import type { ListingDetail } from '@/services/mls/mlsPropertyService';
import type { VowTeaserField } from '@/services/mls/vowOnlyFields';
import type { ListingSurface } from '@/core/analytics/listingEvents';
import { FactList, type FactItem } from '@/components/hub/FactList';
import { HubActionButton } from '@/components/hub/HubActionButton';
import { hubColors, microLabelSx } from '@/components/hub/hubKitStyles';
import { useVowUnlockToken } from '@/hooks/useVowUnlock';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import { useVowGate } from '@/components/vow/useVowGate';
import { layoutTokens } from '@/design-system/tokens/layout';
import { isVowLocked } from './listingSpecs';

const srOnly = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** Bar widths in px. Fixed per kind so nothing about the real value shows. */
export const VOW_PLACEHOLDER_WIDTH = { short: 64, medium: 96, long: 128 } as const;

export function VowLockedValue({ width = 'medium' }: { width?: keyof typeof VOW_PLACEHOLDER_WIDTH }) {
  const { t } = useTranslation();
  return (
    <Box component="span" data-vow-locked sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Box
        component="span"
        aria-hidden
        sx={{
          display: 'inline-block',
          width: VOW_PLACEHOLDER_WIDTH[width],
          height: 12,
          borderRadius: `${layoutTokens.radius.pill}px`,
          backgroundColor: hubColors.borderStrong,
          filter: 'blur(1.5px)',
          userSelect: 'none',
        }}
      />
      <Box component="span" sx={srOnly}>
        {t('listingDetail.vowFacts.hidden', { defaultValue: 'Hidden until you accept the VOW terms' })}
      </Box>
    </Box>
  );
}

/** Row props that make a teaser row one button into the VOW gate. */
export type VowTeaserRow = (field: VowTeaserField, label: string) => Pick<FactItem, 'onActivate' | 'activateLabel'>;

interface GateOptions {
  /** Server-known sign-in state when the caller has it; the client session otherwise. */
  isAuthenticated?: boolean;
  needsReactivation?: boolean;
  surface?: ListingSurface;
  listingKey?: string;
}

export interface VowTeaserGate {
  authed: boolean;
  /** Opens VowAccessModal; signed out at the account step, signed in at the terms and phone step. */
  openGate: (source: 'teaser_row' | 'cta', field?: string) => void;
  teaserRow: VowTeaserRow;
  /** The modal, when no page-level VowMarketAccessProvider owns one. */
  modal: ReactNode;
}

/** Row labels that read badly inside "See {label} …". */
const ROW_NAME: Record<string, string> = { Listed: 'listing date' };

/** Labels like "PID" keep their capitals; the rest read in sentence case. */
const inSentence = (label: string) => ROW_NAME[label] ?? (label === label.toUpperCase() ? label : label.toLowerCase());

export function useVowTeaserGate({ isAuthenticated, needsReactivation = false, surface = 'page', listingKey }: GateOptions = {}): VowTeaserGate {
  const { t } = useTranslation();
  const { authed, openGate, modal } = useVowGate({
    isAuthenticated,
    needsReactivation,
    surface,
    listingKey,
    headline: t('listingDetail.vowFacts.gateHeadline', { defaultValue: 'See every detail of this home' }),
  });

  const teaserRow = useCallback<VowTeaserRow>(
    (field, label) => ({
      onActivate: () => openGate('teaser_row', field),
      activateLabel: authed
        ? t('listingDetail.vowFacts.rowAuthed', { defaultValue: 'Accept the VOW terms to see {{label}}', label: inSentence(label) })
        : t('listingDetail.vowFacts.row', { defaultValue: 'See {{label}} with a free account', label: inSentence(label) }),
    }),
    [authed, openGate, t],
  );

  return { authed, openGate, teaserRow, modal };
}

const sqft = (n: number | null) => (n != null && n > 0 ? `${Math.round(n).toLocaleString('en-CA')} sq ft` : null);
const list = (v: string[] | null) => (v && v.length > 0 ? v.filter(Boolean).join(', ') : null);

interface DeepDiveRow {
  field: VowTeaserField;
  label: string;
  value: string | null;
  width: keyof typeof VOW_PLACEHOLDER_WIDTH;
}

/** Real rows, and bars for the fields the server withheld. Exported for tests. */
export function deepDiveFacts(l: ListingDetail, labels: Record<string, string>, teaser?: VowTeaserRow): FactItem[] {
  const rows: DeepDiveRow[] = [
    { field: 'livingAreaFinishedTotal', label: labels.finished, value: sqft(l.livingAreaFinishedTotal), width: 'short' },
    { field: 'aboveGradeFinishedArea', label: labels.above, value: sqft(l.aboveGradeFinishedArea), width: 'short' },
    { field: 'belowGradeFinishedArea', label: labels.below, value: sqft(l.belowGradeFinishedArea), width: 'short' },
    { field: 'foundationDetails', label: labels.foundation, value: list(l.foundationDetails), width: 'medium' },
    { field: 'sewer', label: labels.sewer, value: list(l.sewer), width: 'medium' },
    { field: 'waterSource', label: labels.water, value: l.waterSource, width: 'medium' },
    { field: 'utilities', label: labels.utilities, value: list(l.utilities), width: 'long' },
    { field: 'zoning', label: labels.zoning, value: l.zoning, width: 'short' },
    { field: 'lotSizeDepth', label: labels.lotDepth, value: l.lotSizeDepth != null && l.lotSizeDepth > 0 ? `${l.lotSizeDepth} ft` : null, width: 'short' },
    { field: 'directionFacesRearYard', label: labels.rearYard, value: l.directionFacesRearYard, width: 'short' },
    { field: 'taxLegalDescription', label: labels.legal, value: l.taxLegalDescription, width: 'long' },
  ];
  const items: FactItem[] = [];
  if (l.rooms && l.rooms.length > 0) {
    for (const r of l.rooms) {
      const dims = r.length && r.width ? `${r.length} × ${r.width}` : '';
      items.push({ label: r.level ? `${r.type} (${r.level})` : r.type, value: dims || null });
    }
  } else if (isVowLocked(l, 'rooms')) {
    items.push({ label: labels.rooms, value: <VowLockedValue width="medium" />, ...teaser?.('rooms', labels.rooms) });
  }
  for (const row of rows) {
    if (row.value) items.push({ label: row.label, value: row.value });
    else if (isVowLocked(l, row.field)) items.push({ label: row.label, value: <VowLockedValue width={row.width} />, ...teaser?.(row.field, row.label) });
  }
  return items;
}

export function VowDeepDiveFacts({ listing, teaser }: { listing: ListingDetail; teaser?: VowTeaserRow }) {
  const { t } = useTranslation();
  const items = deepDiveFacts(listing, {
    rooms: t('listingDetail.vowFacts.rooms', { defaultValue: 'Room sizes' }),
    finished: t('listingDetail.vowFacts.finished', { defaultValue: 'Finished area' }),
    above: t('listingDetail.vowFacts.above', { defaultValue: 'Above grade' }),
    below: t('listingDetail.vowFacts.below', { defaultValue: 'Below grade' }),
    foundation: t('listingDetail.vowFacts.foundation', { defaultValue: 'Foundation' }),
    sewer: t('listingDetail.vowFacts.sewer', { defaultValue: 'Sewer' }),
    water: t('listingDetail.vowFacts.water', { defaultValue: 'Water' }),
    utilities: t('listingDetail.vowFacts.utilities', { defaultValue: 'Utilities' }),
    zoning: t('listingDetail.vowFacts.zoning', { defaultValue: 'Zoning' }),
    lotDepth: t('listingDetail.vowFacts.lotDepth', { defaultValue: 'Lot depth' }),
    rearYard: t('listingDetail.vowFacts.rearYard', { defaultValue: 'Rear yard faces' }),
    legal: t('listingDetail.vowFacts.legal', { defaultValue: 'Legal description' }),
  }, teaser);
  if (items.length === 0) return null;
  return (
    <Box sx={{ mt: 6 }}>
      <Box component="h3" sx={{ ...microLabelSx, mb: 2 }}>
        {t('listingDetail.vowFacts.title', { defaultValue: 'Rooms and building' })}
      </Box>
      <FactList items={items} />
    </Box>
  );
}

/** The section's call to action under the locked rows; the rows open the same gate. */
export function VowUnlockCta({ gate }: { gate: VowTeaserGate }) {
  const { t } = useTranslation();
  const label = gate.authed
    ? t('listingDetail.vowFacts.ctaAuthed', { defaultValue: 'Accept the VOW terms to see these details' })
    : t('listingDetail.vowFacts.cta', { defaultValue: 'See all details with a free account' });

  return (
    <Box data-vow-unlock sx={{ mt: 5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
      <Box component="p" sx={{ m: 0, fontSize: 15, lineHeight: 1.5, color: hubColors.textSecondary, maxWidth: '60ch' }}>
        {t('listingDetail.vowFacts.why', { defaultValue: 'Board rules show these details to signed-in buyers who accept the VOW terms.' })}
      </Box>
      <HubActionButton action={{ label, onClick: () => gate.openGate('cta') }} variant="secondary" />
    </Box>
  );
}

/**
 * The record for Home facts. Without VOW access the facts carry blurred
 * teaser rows; once the VOW modal grants access, the record is fetched again
 * (a unique URL, so no shared cached copy answers) and the real values
 * replace the teasers in place.
 */
export function useVowUnlockedDetail(detail: ListingDetail): ListingDetail {
  const unlockToken = useVowUnlockToken();
  const [fresh, setFresh] = useState<{ key: string; detail: ListingDetail } | null>(null);
  const locked = (detail.vowFieldsPresent?.length ?? 0) > 0;
  const listingKey = detail.listingKey;

  useEffect(() => {
    if (unlockToken === 0 || !locked) return;
    const ctrl = new AbortController();
    fetch(`/api/mls/listings/${encodeURIComponent(listingKey)}?unlock=${unlockToken}-${Date.now()}`, { signal: ctrl.signal, cache: 'no-store' })
      .then((r) => (r.ok ? (r.json() as Promise<ListingDetail>) : null))
      .then((d) => { if (!ctrl.signal.aborted && d) setFresh({ key: listingKey, detail: d }); })
      .catch((err) => {
        if ((err as { name?: string } | undefined)?.name === 'AbortError') return;
        reportSilentFailure(err, { area: 'property-detail/quick-view-vow-unlock', tags: { listingKey } });
      });
    return () => ctrl.abort();
  }, [unlockToken, locked, listingKey]);

  return fresh && fresh.key === listingKey ? fresh.detail : detail;
}
